import { Head, Link } from "@inertiajs/react"
import { useState } from "react"
import PublicLayout from "@/components/public/layout"
import { OptimizedImage } from "@/components/ui/optimized-image"
import type { SeoMeta } from "@/types"
import { getOfficeBySlug, getLocationBySlug, type Office } from "@/data/directory/registry"
import {
  generateServicesProse,
  getLocalMarketContent,
} from "@/data/directory/content-helpers"

/* ------------------------------------------------------------------ */
/* Office logo                                                          */
/* ------------------------------------------------------------------ */

function getInitials(name: string) {
  return name
    .split(/[\s&]+/)
    .filter((w) => w.length > 0 && w[0] === w[0].toUpperCase())
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
}

function getFaviconUrl(website: string, size = 64) {
  try {
    const { hostname } = new URL(website)
    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=${size}`
  } catch {
    return null
  }
}

function OfficeLogo({
  office,
  size = "md",
}: {
  office: Office
  size?: "sm" | "md" | "lg"
}) {
  const [imgFailed, setImgFailed] = useState(false)
  const faviconUrl = getFaviconUrl(office.website)
  const initials = getInitials(office.name)

  const sizeClasses = {
    sm: "size-8 text-xs",
    md: "size-10 text-sm",
    lg: "size-14 text-lg",
  }[size]

  if (faviconUrl && !imgFailed) {
    return (
      <div className={`${sizeClasses} flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-stone bg-white`}>
        <OptimizedImage
          src={faviconUrl}
          alt={`${office.name} logo`}
          className="size-full object-contain p-1"
          onError={() => setImgFailed(true)}
        />
      </div>
    )
  }

  return (
    <div
      className={`${sizeClasses} flex shrink-0 items-center justify-center rounded-lg border border-stone bg-charcoal font-display font-bold text-cream`}
    >
      {initials}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Icons                                                                */
/* ------------------------------------------------------------------ */

function IconStar({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={`size-4 ${filled ? "text-amber-400" : "text-stone"}`}>
      <path
        fillRule="evenodd"
        d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function IconExternalLink() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
      <path
        fillRule="evenodd"
        d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Zm7.49-2.25a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0V4.81l-7.22 7.22a.75.75 0 1 1-1.06-1.06l7.22-7.22H11.74a.75.75 0 0 1-.75-.75Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function IconPhone() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5">
      <path
        fillRule="evenodd"
        d="M2 3.5A1.5 1.5 0 0 1 3.5 2h1.148a1.5 1.5 0 0 1 1.465 1.175l.716 3.223a1.5 1.5 0 0 1-1.052 1.767l-.933.267c-.41.117-.643.555-.48.95a11.542 11.542 0 0 0 6.254 6.254c.395.163.833-.07.95-.48l.267-.933a1.5 1.5 0 0 1 1.767-1.052l3.223.716A1.5 1.5 0 0 1 18 15.352V16.5a1.5 1.5 0 0 1-1.5 1.5H15c-1.149 0-2.263-.15-3.326-.43A13.022 13.022 0 0 1 2.43 8.326 13.019 13.019 0 0 1 2 5V3.5Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function IconEnvelope() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5">
      <path d="M3 4a2 2 0 0 0-2 2v1.161l8.441 4.221a1.25 1.25 0 0 0 1.118 0L19 7.162V6a2 2 0 0 0-2-2H3Z" />
      <path d="m19 8.839-7.77 3.885a2.75 2.75 0 0 1-2.46 0L1 8.839V14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.839Z" />
    </svg>
  )
}

function IconMapPin() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5">
      <path
        fillRule="evenodd"
        d="m9.69 18.933.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 0 0 .281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 1 0 3 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 0 0 2.273 1.765 11.842 11.842 0 0 0 .976.544l.062.029.018.008.006.003ZM10 11.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function IconClock() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function IconCheck() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
      <path
        fillRule="evenodd"
        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/* 404 component                                                        */
/* ------------------------------------------------------------------ */

function NotFound({ location }: { location: string }) {
  return (
    <PublicLayout>
      <section className="px-6 py-32 text-center">
        <h1 className="font-display text-4xl font-bold text-charcoal">Office not found</h1>
        <p className="mt-4 text-slate">
          We couldn&apos;t find that office in our directory.
        </p>
        <Link
          href={`/directory/${location}`}
          className="mt-6 inline-flex items-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red"
        >
          Back to directory
        </Link>
      </section>
    </PublicLayout>
  )
}

/* ------------------------------------------------------------------ */
/* Similar offices                                                      */
/* ------------------------------------------------------------------ */

function SimilarOffices({ office, location }: { office: Office; location: string }) {
  const locationData = getLocationBySlug(location)
  const allOffices = locationData?.offices ?? []

  const similar = allOffices.filter(
    (o) => o.slug !== office.slug && o.areas.some((a) => office.areas.includes(a))
  ).slice(0, 3)

  if (similar.length === 0) return null

  return (
    <section className="border-t border-stone px-6 py-20">
      <div className="mx-auto max-w-6xl">
        <h2 className="font-display text-2xl font-bold text-charcoal">
          Other agents in the area<span className="text-red">.</span>
        </h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {similar.map((o) => (
            <Link
              key={o.slug}
              href={`/directory/${location}/${o.slug}`}
              className="group rounded-lg border border-stone p-5 transition-all hover:border-stone/80 hover:shadow-sm"
            >
              <div className="flex items-center gap-3">
                <OfficeLogo office={o} size="sm" />
                <div>
                  <p className="font-display text-lg font-bold text-charcoal group-hover:text-red transition-colors">
                    {o.name}
                  </p>
                  <p className="text-sm text-slate">{o.branch}</p>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <IconStar key={star} filled={star <= Math.round(o.rating)} />
                  ))}
                </div>
                <span className="text-sm font-medium text-charcoal">{o.rating}</span>
              </div>
              <p className="mt-2 text-xs text-warm-grey">
                {o.specialisms.slice(0, 2).join(" · ")}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export default function DirectoryShow({
  meta,
  location,
  slug,
}: {
  meta: SeoMeta
  location: string
  slug: string
}) {
  const office = getOfficeBySlug(location, slug)
  const locationData = getLocationBySlug(location)
  const cityName = locationData?.name ?? location

  if (!office) return <NotFound location={location} />

  const fullName = `${office.name} - ${office.branch}`
  const servicesProse = generateServicesProse(office, cityName)
  const localMarket = getLocalMarketContent(office, location, cityName)

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            name: fullName,
            description: office.aboutText ?? office.description,
            address: {
              "@type": "PostalAddress",
              streetAddress: office.address,
              addressLocality: cityName,
              postalCode: office.postcode,
              addressCountry: "GB",
            },
            telephone: office.phone,
            email: office.email,
            url: office.website,
            foundingDate: String(office.established),
            areaServed: office.areas.map((a) => ({
              "@type": "City",
              name: a,
            })),
            knowsAbout: office.specialisms,
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: office.rating,
              reviewCount: office.reviewCount,
              bestRating: 5,
            },
            openingHoursSpecification: office.hours.map((h) => ({
              "@type": "OpeningHoursSpecification",
              dayOfWeek: h.days,
              opens: h.time.split("\u2013")[0],
              closes: h.time.split("\u2013")[1] || "Closed",
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${new URL(meta.canonical).origin}/` },
              { "@type": "ListItem", position: 2, name: "Directory", item: `${new URL(meta.canonical).origin}/directory` },
              { "@type": "ListItem", position: 3, name: cityName, item: `${new URL(meta.canonical).origin}/directory/${location}` },
              { "@type": "ListItem", position: 4, name: office.name },
            ],
          })}
        </script>
        {office.faq && office.faq.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: office.faq.map((item) => ({
                "@type": "Question",
                name: item.question,
                acceptedAnswer: { "@type": "Answer", text: item.answer },
              })),
            })}
          </script>
        )}
      </Head>

      {/* Breadcrumb */}
      <div className="border-b border-stone bg-cream/50 px-6 py-3">
        <div className="mx-auto max-w-6xl">
          <nav className="flex items-center gap-2 text-sm text-warm-grey">
            <Link href="/directory" className="transition-colors hover:text-charcoal">
              Directory
            </Link>
            <span>/</span>
            <Link href={`/directory/${location}`} className="transition-colors hover:text-charcoal">
              {cityName}
            </Link>
            <span>/</span>
            <span className="text-charcoal">{fullName}</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <section className="px-6 py-16 md:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between md:gap-12">
            {/* Left */}
            <div className="flex-1">
              <div className="flex items-center gap-4">
                <OfficeLogo office={office} size="lg" />
                <div>
                  <h1 className="font-display text-4xl font-bold tracking-tight text-charcoal md:text-5xl">
                    {office.name}<span className="text-red">.</span>
                  </h1>
                  <p className="mt-1 text-lg text-slate">{office.branch}, {cityName}</p>
                </div>
              </div>

              {/* Rating */}
              <div className="mt-4 flex items-center gap-3">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <IconStar key={star} filled={star <= Math.round(office.rating)} />
                  ))}
                </div>
                <span className="text-lg font-semibold text-charcoal">{office.rating}</span>
                <span className="text-sm text-warm-grey">({office.reviewCount} reviews)</span>
              </div>

              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate">
                {office.description}
              </p>

              {/* Website backlink */}
              <div className="mt-6">
                <a
                  href={office.website}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-2 rounded-lg bg-red px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-deep-red hover:shadow-md hover:shadow-red/10"
                >
                  Visit {office.name} website
                  <IconExternalLink />
                </a>
              </div>
            </div>

            {/* Right — contact card */}
            <div className="w-full shrink-0 rounded-xl border border-stone bg-cream p-6 md:w-80">
              <h2 className="font-display text-lg font-bold text-charcoal">Contact details</h2>

              <div className="mt-5 space-y-4">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 text-red"><IconMapPin /></span>
                  <div>
                    <p className="text-sm font-medium text-charcoal">{office.address}</p>
                    <p className="text-sm text-slate">{cityName}, {office.postcode}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-red"><IconPhone /></span>
                  <a
                    href={`tel:${office.phone.replace(/\s/g, "")}`}
                    className="text-sm font-medium text-charcoal transition-colors hover:text-red"
                  >
                    {office.phone}
                  </a>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-red"><IconEnvelope /></span>
                  <a
                    href={`mailto:${office.email}`}
                    className="text-sm font-medium text-charcoal transition-colors hover:text-red"
                  >
                    {office.email}
                  </a>
                </div>

                <div className="flex items-start gap-3">
                  <span className="mt-0.5 text-red"><IconClock /></span>
                  <div className="space-y-0.5">
                    {office.hours.map((h) => (
                      <div key={h.days} className="flex items-center gap-2 text-sm">
                        <span className="w-16 font-medium text-charcoal">{h.days}</span>
                        <span className="text-slate">{h.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Backlink */}
              <a
                href={office.website}
                target="_blank"
                rel="noopener"
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg border border-stone bg-white px-4 py-2.5 text-sm font-medium text-charcoal transition-colors hover:bg-stone"
              >
                Visit website
                <IconExternalLink />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-12 md:grid-cols-2">
            {/* Specialisms */}
            <div>
              <h2 className="font-display text-xl font-bold text-charcoal">Specialisms</h2>
              <div className="mt-4 space-y-2">
                {office.specialisms.map((s) => (
                  <div key={s} className="flex items-center gap-2.5">
                    <span className="text-red"><IconCheck /></span>
                    <span className="text-sm text-charcoal">{s}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Areas covered */}
            <div>
              <h2 className="font-display text-xl font-bold text-charcoal">Areas covered</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {office.areas.map((a) => (
                  <Link
                    key={a}
                    href={`/directory/${location}?area=${encodeURIComponent(a)}`}
                    className="rounded-full border border-stone bg-cream px-3.5 py-1.5 text-sm text-charcoal transition-colors hover:border-red/30 hover:bg-red/5 hover:text-red"
                  >
                    {a}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Key facts */}
          <div className="mt-12 grid grid-cols-2 gap-6 rounded-xl border border-stone bg-cream p-6 md:grid-cols-4">
            <div className="text-center">
              <p className="font-display text-2xl font-bold text-charcoal">{office.established}</p>
              <p className="mt-1 text-xs text-warm-grey">Established</p>
            </div>
            <div className="text-center">
              <p className="font-display text-2xl font-bold text-charcoal">~{office.propertyCount}</p>
              <p className="mt-1 text-xs text-warm-grey">Active listings</p>
            </div>
            <div className="text-center">
              <p className="font-display text-2xl font-bold text-charcoal">{office.avgPrice}</p>
              <p className="mt-1 text-xs text-warm-grey">Average price</p>
            </div>
            <div className="text-center">
              <p className="font-display text-2xl font-bold text-charcoal">{office.rating}/5</p>
              <p className="mt-1 text-xs text-warm-grey">{office.reviewCount} reviews</p>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="border-t border-stone bg-cream/50 px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            About {office.name} in {office.branch}<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-4 text-base leading-relaxed text-slate">
            {(office.aboutText ?? office.description).split("\n\n").map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Services prose */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Services and specialisms<span className="text-red">.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate">
            {servicesProse}
          </p>
        </div>
      </section>

      {/* Local market */}
      <section className="border-t border-stone bg-cream/50 px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            {localMarket.heading}<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-4 text-base leading-relaxed text-slate">
            {localMarket.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          {localMarket.areaGuideHref && (
            <Link
              href={localMarket.areaGuideHref}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-red transition-colors hover:text-deep-red"
            >
              Read the full {localMarket.areaGuideName} guide
              <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
                <path
                  fillRule="evenodd"
                  d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
          )}
        </div>
      </section>

      {/* Why choose */}
      {office.whyChoose && office.whyChoose.length > 0 && (
        <section className="border-t border-stone bg-white px-6 py-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-display text-2xl font-bold text-charcoal">
              Why choose {office.name}<span className="text-red">?</span>
            </h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {office.whyChoose.map((reason) => (
                <div key={reason} className="flex items-start gap-3">
                  <span className="mt-0.5 text-red"><IconCheck /></span>
                  <span className="text-sm leading-relaxed text-charcoal">{reason}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {office.faq && office.faq.length > 0 && (
        <section className="border-t border-stone bg-cream/50 px-6 py-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-display text-2xl font-bold text-charcoal">
              Frequently asked questions<span className="text-red">.</span>
            </h2>
            <div className="mt-8 divide-y divide-stone">
              {office.faq.map((item) => (
                <details key={item.question} className="group py-5">
                  <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                    {item.question}
                    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180">
                      <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                    </svg>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-slate">{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Similar offices */}
      <SimilarOffices office={office} location={location} />

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            {office.name}, meet QuiteRight<span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-warm-grey">
            Turn your listings into professional video ads. Paste a URL, pick a template, and
            publish across every social platform - in under a minute.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Request a demo
            </Link>
            <Link
              href={`/directory/${location}`}
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-6 py-3 text-base font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              Back to directory
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
